import React from "react";
import { Input, Button, Select, SelectItem } from "@heroui/react";
import { Link, MetaFunction, useActionData, Form, useNavigation } from "@remix-run/react";
import { SignUpSchema } from "app/schema/signup.schema";
import { statesData, cityData } from "app/mock/signUpData";
import { signupApi } from "app/api/auth.api";
import { redirect, json } from "@remix-run/node";
import { ActionFunction } from "@remix-run/node";
import { commitSession, getSession } from "app/auth/sessions";
import { toast, ToastContainer } from "react-toastify";
import { SEOHandle } from "@nasa-gcn/remix-seo";
import { getSocialMetaTags } from "app/utils/meta";

export const meta: MetaFunction = () =>
  getSocialMetaTags({
    title: "Sign Up | Promotional Products Now",
    description: "Welcome to Promotional Products Now",
    url: "https://www.promotionalproductsnow.au/signUp",

    extra: [
      { tagName: "link", rel: "canonical", href: "https://www.promotionalproductsnow.au/signUp" },
    ],
  });

interface ActionData {
  success: boolean;
  errors?: Record<string, string>;
  error?: string;
}

export const handle: SEOHandle = {
  getSitemapEntries: async () => [
    {
      route: "/signup",
      priority: 0.6,
      lastmod: new Date().toISOString(),
      changefreq: "monthly",
    },
  ],
};

export const action: ActionFunction = async ({ request }) => {
  const session = await getSession(request.headers.get("Cookie"));
  const formData = await request.formData();
  const formDataObject = Object.fromEntries(formData.entries());

  try {
    await SignUpSchema.validate(formDataObject, { abortEarly: false });

    const {
      email,
      phone,
      password,
      confirmPassword,
      firstName,
      lastName,
      apartment,
      streetAddress,
      city,
      state,
      postCode,
      timeZone,
      companyName,
    } = formDataObject;

    const response = await signupApi({
      email: email as string,
      phone: phone as string,
      password: password as string,
      confirmPassword: confirmPassword as string,
      firstName: firstName as string,
      lastName: lastName as string,
      companyName: companyName as string,
      location: {
        apartment: apartment as string,
        streetAddress: streetAddress as string,
        city: city as string,
        state: state as string,
        postCode: postCode as string,
        timeZone: timeZone as string,
      },
    });

    if (response.data.payload) {
      session.set("email", response.data.payload);
      return redirect("/otp", {
        headers: {
          "Set-Cookie": await commitSession(session),
        },
      });
    }
  } catch (error: any) {
    if (error.name === "ValidationError") {
      const errors = error.inner.reduce((acc: Record<string, string>, curr: any) => {
        acc[curr.path] = curr.message;
        return acc;
      }, {});
      return { success: false, errors };
    }
    const errorMessage = Array.isArray(error?.response?.data?.message)
      ? error.response.data.message.join(", ") // Convert array to a readable string
      : error?.response?.data?.message || "An error occurred during registration";

    return {
      success: false,
      error: errorMessage,
    };
  }
};

export default function SignUp(): JSX.Element {
  const navigation = useNavigation();
  const actionData = useActionData<ActionData>();
  const [timeZone, setTimeZone] = React.useState<string>("");
  const [localErrors, setLocalErrors] = React.useState<Record<string, string>>({});
  const errors = localErrors;

  const getTimeZone = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimeZone(timezone);
  };

  React.useEffect(() => {
    getTimeZone();

    if (actionData?.error) {
      toast.error(actionData.error, {
        containerId: "loginToast",
      });
    }
  }, [actionData]);

  React.useEffect(() => {
    if (actionData?.errors) {
      setLocalErrors(actionData.errors);
    }
  }, [actionData]);

  const handleInputChange = (fieldName: string) => {
    // Clear error when user starts typing

    setLocalErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  return (
    <div className="py-8 mt-12 md:mt-0">
      <ToastContainer containerId="loginToast" />
      <div className="space-y-4 px-2">
        <h1 className="text-2xl md:text-3xl xl:text-4xl 2xl:text-5xl 4xl:text-6xl text-dark font-bold text-center">
          Welcome to Promotional Products Now
        </h1>
        <div>
          <p className="text-base md:text-xl text-dark text-center font-medium">
            Create your free account for a smoother website browsing and purchasing experience
          </p>
          <p className="text-center text-dark text-base md:text-xl font-semibold">
            Have an account?{" "}
            <Link to="/login" className="text-orange-700 dark:text-orange-300 font-bold">
              Login
            </Link>
          </p>
        </div>
      </div>

      <Form
        method="post"
        className="w-full overflow-hidden py-4 px-4 md:py-12 flex flex-col items-center justify-center"
      >
        <input type="hidden" name="timeZone" value={timeZone} />

        <div className="w-full md:w-4/5 xl:w-3/5 2xl:w-2/4 overflow-hidden py-4 md:py-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full">
              <p className="text-base font-semibold text-dark">FIRST NAME</p>
              <Input
                name="firstName"
                type="text"
                variant="underlined"
                isInvalid={!!errors?.firstName}
                labelPlacement="outside"
                size="lg"
                placeholder="Your first name"
                color="primary"
                errorMessage={errors?.firstName}
                onInput={() => handleInputChange("firstName")}
              />
            </div>
            <div className="w-full">
              <p className="text-base font-semibold text-dark">LAST NAME</p>
              <Input
                name="lastName"
                type="text"
                isInvalid={!!errors?.lastName}
                variant="underlined"
                labelPlacement="outside"
                size="lg"
                placeholder="Your last name"
                color="primary"
                errorMessage={errors?.lastName}
                onInput={() => handleInputChange("lastName")}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full">
              <p className="text-base font-semibold text-dark">EMAIL ADDRESS</p>
              <Input
                name="email"
                type="email"
                isInvalid={!!errors?.email}
                variant="underlined"
                labelPlacement="outside"
                size="lg"
                placeholder="Your email"
                color="primary"
                errorMessage={errors?.email}
                onInput={() => handleInputChange("email")}
              />
            </div>
            <div className="w-full">
              <p className="text-base font-semibold text-dark">CONFIRM EMAIL ADDRESS</p>
              <Input
                name="confirmEmail"
                type="email"
                isInvalid={!!errors?.confirmEmail}
                variant="underlined"
                labelPlacement="outside"
                size="lg"
                placeholder="Confirm email"
                color="primary"
                errorMessage={errors?.confirmEmail}
                onInput={() => handleInputChange("confirmEmail")}
                onPaste={(e) => e.preventDefault()} // Prevent pasting
                className="transition-all duration-300 hover:border-primary focus:border-secondary"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full">
              <p className="text-base font-semibold text-dark">Company</p>
              <Input
                name="companyName"
                type="text"
                isInvalid={!!errors?.companyName}
                variant="underlined"
                labelPlacement="outside"
                size="lg"
                placeholder="Company Name"
                color="primary"
                errorMessage={errors?.companyName}
                onInput={() => handleInputChange("companyName")}
              />
            </div>

            <div className="w-full">
              <p className="text-base font-semibold text-dark">PHONE</p>
              <Input
                name="phone"
                type="text"
                isInvalid={!!errors?.phone}
                variant="underlined"
                labelPlacement="outside"
                size="lg"
                placeholder="Your phone"
                color="primary"
                errorMessage={errors?.phone}
                onInput={() => handleInputChange("phone")}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full">
              <p className="text-base font-semibold text-dark">PASSWORD</p>
              <Input
                name="password"
                type="password"
                variant="underlined"
                isInvalid={!!errors?.password}
                labelPlacement="outside"
                size="lg"
                placeholder="Your password"
                color="primary"
                errorMessage={errors?.password}
                onInput={() => handleInputChange("password")}
              />
            </div>
            <div className="w-full">
              <p className="text-base font-semibold text-dark">REPEAT PASSWORD</p>
              <Input
                name="confirmPassword"
                type="password"
                variant="underlined"
                isInvalid={!!errors?.confirmPassword}
                labelPlacement="outside"
                size="lg"
                placeholder="Confirm password"
                color="primary"
                errorMessage={errors?.confirmPassword}
                onInput={() => handleInputChange("confirmPassword")}
              />
            </div>
          </div>

          <div className="w-full">
            <p className="text-base font-semibold text-dark">
              Apartment, Suite, Unit, etc. (optional)
            </p>
            <Input
              name="apartment"
              type="text"
              variant="underlined"
              isInvalid={!!errors?.apartment}
              labelPlacement="outside"
              size="lg"
              placeholder="Your Apartment"
              color="primary"
              errorMessage={errors?.apartment}
              onInput={() => handleInputChange("apartment")}
            />
          </div>

          <div className="w-full">
            <p className="text-base font-semibold text-dark">Street Address</p>
            <Input
              name="streetAddress"
              type="text"
              variant="underlined"
              isInvalid={!!errors?.streetAddress}
              labelPlacement="outside"
              size="lg"
              placeholder="Your Street address"
              color="primary"
              errorMessage={errors?.streetAddress}
              onInput={() => handleInputChange("streetAddress")}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="w-full">
              <p className="text-base font-semibold text-dark">SUBURB</p>
              <Input
                name="city"
                type="text"
                aria-label="city"
                isInvalid={!!errors?.city}
                variant="underlined"
                placeholder="Your suburb"
                color="primary"
                size="lg"
                errorMessage={errors?.city}
                onInput={() => handleInputChange("city")}
              />
            </div>
            <div className="w-full">
              <p className="text-base font-semibold text-dark">STATE</p>
              <Select
                name="state"
                aria-label="state"
                variant="underlined"
                placeholder="Your state"
                color="primary"
                size="lg"
                errorMessage={errors.state}
                showScrollIndicators={true}
                scrollShadowProps={{ orientation: "vertical", hideScrollBar: false, hidden: false }}
                isInvalid={!!errors.state}
                onChange={() => handleInputChange("state")}
              >
                {statesData.map((state) => (
                  <SelectItem key={state.value}>{state.label}</SelectItem>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <p className="text-base font-semibold text-dark">POST CODE</p>
            <Input
              name="postCode"
              type="text"
              variant="underlined"
              isInvalid={!!errors?.postCode}
              labelPlacement="outside"
              size="lg"
              placeholder="Your post code"
              color="primary"
              errorMessage={errors?.postCode}
              onInput={() => handleInputChange("postCode")}
            />
          </div>

          <div className="py-6 w-full">
            <Button
              type="submit"
              variant="solid"
              color="primary"
              className="font-semibold w-full hover:scale-[1.01] transition-transform duration-200"
              size="lg"
              isLoading={navigation.state === "submitting"}
              disabled={navigation.state === "submitting"}
            >
              {navigation.state === "submitting"
                ? "Creating Your Account..."
                : "CREATE YOUR ACCOUNT"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}
